import { useState, useEffect, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/Modal/dialog/dialog'
import modalScss from '../../containers/CSSModules/Modal.scss'
import Button from '@/components/Button'

/**
 * SharedModal - A reusable modal component for consistent UI across the application
 *
 * @param {Object} props
 * @param {string} props.title - The title of the modal
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {Function} [props.onSave] - Function to call when the save button is clicked
 * @param {React.ReactNode} props.children - The content of the modal
 * @param {string} [props.size='md'] - The size of the modal (sm, md, lg, xl, 2xl, 3xl, 4xl, 5xl, full)
 * @param {string} [props.saveButtonText='Save'] - The text for the save button
 * @param {string} [props.cancelButtonText='Cancel'] - The text for the cancel button
 * @param {boolean} [props.isSaving=false] - Whether the save operation is in progress
 * @param {boolean} [props.isFormValid=true] - Whether the form is valid
 * @param {boolean} [props.hideFooter=false] - Whether to hide the footer
 * @param {React.ReactNode} [props.footerContent] - Custom footer content
 * @param {boolean} [props.fullWidthButton=false] - Whether the save button should take full width
 * @param {import('./SharedModal').FooterActionButton[]} [props.footerActionButtons] - Custom footer action buttons
 * @param {boolean} [props.backgroundClickDismiss=true] - Whether clicking the background should close the modal
 * @param {boolean} [props.closeOnDismiss=true] - Hide immediately on dismiss; false lets the parent finish asynchronous cleanup before unmounting.
 * @param {boolean} [props.noPadding=false] - Whether to remove default content padding (for edge-to-edge layouts)
 * @param {boolean} [props.scrollBody=false] - Whether the modal body should scroll within the viewport
 */
const SharedModal = ({
  title,
  description,
  isOpen,
  onClose,
  onSave,
  children,
  size = 'md',
  saveButtonText,
  cancelButtonText,
  isSaving = false,
  isFormValid = true,
  hideFooter = false,
  footerContent,
  fullWidthButton = false,
  footerActionButtons = null,
  backgroundClickDismiss = true,
  closeOnDismiss = true,
  noPadding = false,
  scrollBody = false,
  dataTestId,
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(isOpen)

  // Set default button text using translations
  const defaultSaveText = saveButtonText || t('common.save')
  const defaultCancelText = cancelButtonText || t('common.cancel')

  useEffect(() => {
    setOpen(isOpen)
  }, [isOpen])

  const handleClose = useCallback(() => {
    if (closeOnDismiss) setOpen(false)
    onClose()
  }, [closeOnDismiss, onClose])

  // Default footer content that mimics the original modal styling
  const defaultFooter = useMemo(() => {
    if (footerActionButtons) {
      return (
        <>
          {footerActionButtons.map(
            (
              { label, variant, onClick, disabled, loading, ...rest },
              index
            ) => (
              <Button
                key={label + index}
                variant={variant}
                size='lg'
                label={label}
                onClick={onClick}
                disabled={disabled}
                loading={loading}
                {...rest}
              />
            )
          )}
        </>
      )
    }
    return (
      <>
        {!fullWidthButton ? (
          <>
            <Button
              variant='secondary'
              size='lg'
              label={defaultCancelText}
              onClick={handleClose}
              data-testid='modal-cancel-button'
            />
            <Button
              variant='primary'
              size='lg'
              label={defaultSaveText}
              loading={isSaving}
              onClick={onSave}
              disabled={isSaving || !isFormValid}
              data-testid='modal-save-button'
            />
          </>
        ) : (
          <Button
            variant='primary'
            size='lg'
            label={defaultSaveText}
            loading={isSaving}
            onClick={onSave}
            disabled={isSaving || !isFormValid}
            data-testid='modal-save-button'
            style={{ width: '100%', placeContent: 'center' }}
          />
        )}
      </>
    )
  }, [
    footerActionButtons,
    fullWidthButton,
    defaultCancelText,
    handleClose,
    defaultSaveText,
    isSaving,
    onSave,
    isFormValid,
  ])

  return (
    <Dialog open={open} onOpenChange={openState => !openState && handleClose()}>
      <DialogContent
        data-testid={dataTestId}
        data-floating-ui-dismiss-ignore='true'
        size={size}
        className={scrollBody ? 'p-0 max-h-[calc(100vh-4rem)]' : 'p-0'}
        onInteractOutside={event => {
          if (!backgroundClickDismiss) {
            event.preventDefault()
            return
          }
          const { target } = event
          if (
            target instanceof Element &&
            (target.closest('#react-select-portal') ||
              target.closest('[class*="reactSelect__menu"]'))
          ) {
            event.preventDefault()
          }
        }}
      >
        <div className='flex flex-col bg-[#1a2937] max-h-full overflow-hidden min-w-0'>
          <div className='border-b border-[#626971] rounded-t-lg'>
            <DialogTitle onClose={handleClose}>{title}</DialogTitle>
            {description && (
              <DialogDescription>
                <span className='block text-sm text-white m-0 leading-relaxed'>
                  {description}
                </span>
              </DialogDescription>
            )}
          </div>

          <div
            className={[
              noPadding ? '' : 'p-[24px_32px] mt-2',
              scrollBody
                ? `${modalScss.overflowY} flex-1 min-h-0`
                : modalScss.MuiDialogBoxes,
            ].join(' ')}
          >
            {children}
          </div>

          {!hideFooter && (
            <DialogFooter className='border-t border-[#626971] justify-between shrink-0'>
              {footerContent || defaultFooter}
            </DialogFooter>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default SharedModal
